import React from 'react'
import { Formik } from 'formik'

import { AdminContext } from 'contexts/AdminContext'

import { ZariContext } from '_zari/context'
import { Button, Form, Col, Row, Divider } from '_zari/core'

import { MonedasConstant, MontosIcbper } from 'constants/index'

import { IdGen, RounderUtil } from 'utils'

import * as PreFacturacionService from 'services/preFacturacion.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

import { ValidationSchema } from './validation'
import commonReducer from './reducer'

const RESUMEN_MONTOS_INITIAL_VALUES = {
  sumaInafecto: 0,
  sumaExonerado: 0,
  sumaGratuito: 0,
  sumaGravado: 0,
  sumaIgv: 0,
  sumaDescuento: 0,
  sumaIcbper: 0,
  sumaAnticipo: 0,
  sumaTotal: 0
}

const EstandarView = ({ editarData, flagEditar, reiniciarFormulario }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editarData.detalles, inputArr => {
    if (inputArr) {
      const salida = inputArr.map(x => ({
        ...x,
        afectacionBase: x.afectacion,
        __filaActiva: false,
        __focusCantidad: false,
        __focusDescuento: false,
        __focusPrecio: false,
        esFilaBusqueda: false,
        _idFila: IdGen.newId()
      }))

      return [...salida, { __filaActiva: true, esFilaBusqueda: true, _idFila: IdGen.newId() }]
    }
    return [{ __filaActiva: true, esFilaBusqueda: true, _idFila: IdGen.newId() }]
  })

  const [__keyResumenMontos, setKeyResumenMontos] = React.useState(IdGen.newId())
  const [mostrarBotonRecalcular, setMostrarBotonRecalcular] = React.useState(false)

  const mainFormikRef = React.useRef(null)
  const resumenMontosRef = React.useRef(RESUMEN_MONTOS_INITIAL_VALUES)
  const firstRefreshRef = React.useRef(false)

  const formInitialValues = React.useMemo(() => {
    return {
      _id: editarData._id || '',
      _idListaPrecio: editarData._idListaPrecio || __adminContext.valoresConfiguracionSede._idListaPrecio || '',
      nombre: editarData.nombre || '',
      incluyeIGV: true,
      moneda: MonedasConstant.SOLES
    }
  }, [editarData, __adminContext.valoresConfiguracionSede._idListaPrecio])

  const clickRecalcular = React.useCallback(() => {
    const incluyeIGV = mainFormikRef.current.values.incluyeIGV
    const montoAnticipo = mainFormikRef.current.values.montoAnticipo

    let sumaInafecto = 0
    let sumaExonerado = 0
    let sumaGratuito = 0
    let sumaGravado = 0
    let sumaIgv = 0
    let sumaDescuento = 0
    let sumaIcbper = 0
    let sumaAnticipo = 0
    let sumaTotal = 0

    let detTotal = 0

    const fechaActual = new Date()
    const anoActual = `${fechaActual.getFullYear()}`

    let detalle = null
    let factorIcbper = MontosIcbper[anoActual] || MontosIcbper['2023']

    const nuevosDetalles = [...detalles].filter(x => !x.esFilaBusqueda)

    if (incluyeIGV) {
      for (let i = 0; i < nuevosDetalles.length; i++) {
        detalle = nuevosDetalles[i]

        if (detalle.flagGratuito) {
          // Gratuito
          sumaGratuito = RounderUtil.round(sumaGratuito + parseFloat(detalle.total))
        } else {
          // Descuento
          sumaDescuento = RounderUtil.round(sumaDescuento + parseFloat(detalle.descuentoMonto || '0'))

          // Icbper
          sumaIcbper = RounderUtil.round(
            sumaIcbper + parseFloat(detalle.cantidad) * (detalle.flagBolsa ? factorIcbper : 0)
          )

          if (detalle.afectacion === '20') {
            sumaExonerado = RounderUtil.round(sumaExonerado + parseFloat(detalle.subTotal))
          } else if (detalle.afectacion === '30') {
            sumaInafecto = RounderUtil.round(sumaInafecto + parseFloat(detalle.subTotal))
          } else {
            const igv = RounderUtil.round(parseFloat(detalle.subTotal) - parseFloat(detalle.subTotal) / 1.18)
            const gravado = RounderUtil.round(parseFloat(detalle.subTotal) - igv)

            // Igv
            sumaIgv = RounderUtil.round(sumaIgv + parseFloat(igv || '0'))
            sumaGravado = RounderUtil.round(sumaGravado + parseFloat(gravado || '0'))
          }

          // Total
          sumaTotal = RounderUtil.round(sumaTotal + parseFloat(detalle.total))
        }
      }
    } else {
      for (let i = 0; i < nuevosDetalles.length; i++) {
        detalle = nuevosDetalles[i]

        if (detalle.flagGratuito) {
          // Gratuito
          sumaGratuito = RounderUtil.round(sumaGratuito + parseFloat(detalle.total))
        } else {
          // Descuento
          sumaDescuento = RounderUtil.round(sumaDescuento + parseFloat(detalle.descuentoMonto || '0'))

          // Icbper
          sumaIcbper = RounderUtil.round(
            sumaIcbper + parseFloat(detalle.cantidad) * (detalle.flagBolsa ? factorIcbper : 0)
          )

          if (detalle.afectacion === '20') {
            sumaExonerado = RounderUtil.round(sumaExonerado + parseFloat(detalle.subTotal))
          } else if (detalle.afectacion === '30') {
            sumaInafecto = RounderUtil.round(sumaInafecto + parseFloat(detalle.subTotal))
          } else {
            const gravado = RounderUtil.round(
              parseFloat(detalle.precioReferencia || detalle.precio) * parseFloat(detalle.cantidad)
            )
            const igv = RounderUtil.round(gravado * 0.18)

            // Igv
            sumaIgv = RounderUtil.round(sumaIgv + parseFloat(igv || '0'))
            sumaGravado = RounderUtil.round(sumaGravado + parseFloat(gravado || '0'))
            detTotal = RounderUtil.round(igv + gravado)
          }

          // Total
          sumaTotal = RounderUtil.round(sumaTotal + parseFloat(detTotal))
          detTotal = 0
        }
      }
    }

    if (montoAnticipo) {
      sumaAnticipo = parseFloat(montoAnticipo)
      const anticipoMontoBase = RounderUtil.round(sumaAnticipo / 1.18)
      const igvAnticipo = RounderUtil.round(anticipoMontoBase * 0.18)

      sumaGravado = RounderUtil.round(sumaGravado - anticipoMontoBase)
      sumaIgv = RounderUtil.round(sumaIgv - igvAnticipo)
      sumaTotal = RounderUtil.round(sumaTotal - sumaAnticipo)
    }

    sumaTotal = RounderUtil.round(sumaTotal + sumaIcbper)

    resumenMontosRef.current = {
      sumaInafecto,
      sumaExonerado,
      sumaGratuito,
      sumaGravado,
      sumaIgv,
      sumaDescuento,
      sumaIcbper,
      sumaAnticipo,
      sumaTotal
    }

    setKeyResumenMontos(IdGen.newId())
    setMostrarBotonRecalcular(false)
  }, [detalles])

  const reiniciarFormularioMontos = React.useCallback(() => {
    setMostrarBotonRecalcular(true)
    resumenMontosRef.current = RESUMEN_MONTOS_INITIAL_VALUES
    setKeyResumenMontos(IdGen.newId())
  }, [])

  const onSubmit = React.useCallback(
    (formData, { setSubmitting } = {}) => {
      const nuevosDetalles = [...detalles]

      for (let i = 0; i < nuevosDetalles.length; i++) {
        const nuevoDet = nuevosDetalles[i]

        if (nuevoDet.flagGratuito && nuevoDet.afectacionBase !== '10') {
          __zariContext.snack('error', 'Solo se puede aplicar monto gratuito a productos con afectación 10.')
          setSubmitting(false)
          return
        }
      }

      const detallesNormalizados = nuevosDetalles
        .filter(x => !x.esFilaBusqueda)
        .map(x => {
          let precio = 0
          let valorUnitario = 0
          let gravado = 0
          let exonerado = 0
          let inafecto = 0
          let valorBrutoTotal = 0
          let igv = 0
          let total = 0
          let precioReferencia = 0

          let descuentoConIGV = 0
          let descuentoSinIGV = 0
          let descuentoIGV = 0

          if (x.afectacion === '10' || x.afectacion === '11') {
            if (formData.incluyeIGV) {
              precio = parseFloat(x.precio || 0)
              precioReferencia = precio
              valorUnitario = RounderUtil.round(parseFloat(x.precio || 0) / 1.18, 5)
              igv = RounderUtil.round(parseFloat(x.subTotal) - parseFloat(x.subTotal) / 1.18)
              gravado = RounderUtil.round(parseFloat(x.subTotal) - igv)
              valorBrutoTotal = gravado
              total = RounderUtil.round(parseFloat(x.total))
            } else {
              valorUnitario = parseFloat(x.precio || 0)
              precio = RounderUtil.round(valorUnitario * 1.18)
              precioReferencia = precio
              gravado = parseFloat(x.subTotal || 0)
              valorBrutoTotal = gravado
              igv = RounderUtil.round(gravado * 0.18)
              total = RounderUtil.round(gravado + igv)
            }
          } else if (x.afectacion === '20') {
            valorUnitario = parseFloat(x.precio || 0)
            precio = RounderUtil.round(valorUnitario)
            precioReferencia = precio
            exonerado = parseFloat(x.subTotal || 0)
            valorBrutoTotal = exonerado
            igv = 0
            total = exonerado
          } else if (x.afectacion === '30') {
            valorUnitario = parseFloat(x.precio || 0)
            precio = RounderUtil.round(valorUnitario)
            precioReferencia = precio
            inafecto = parseFloat(x.subTotal || 0)
            valorBrutoTotal = inafecto
            igv = 0
            total = inafecto
          }

          if (formData.incluyeIGV) {
            descuentoConIGV = parseFloat(x.descuentoMonto ?? 0)
            descuentoSinIGV = RounderUtil.round(x.descuentoMonto / 1.18, 5)
            descuentoIGV = RounderUtil.round(descuentoConIGV - descuentoSinIGV, 5)
          } else {
            descuentoSinIGV = parseFloat(x.descuentoMonto ?? 0)
            descuentoIGV = RounderUtil.round(descuentoSinIGV * 0.18, 5)
            descuentoConIGV = RounderUtil(descuentoSinIGV + descuentoIGV, 5)
          }

          return {
            _id: x._id,
            _idCentroCostoNivel1: x._idCentroCostoNivel1,
            _idCentroCostoNivel2: x._idCentroCostoNivel2,
            _idProductoPresentacion: x._idProductoPresentacion,
            cantidad: parseFloat(x.cantidad || 0),
            precio: precio,
            valorUnitario: valorUnitario,
            gravado: gravado,
            exonerado: exonerado,
            inafecto: inafecto,
            valorBrutoTotal: valorBrutoTotal,
            igv: igv,
            total: total,
            precioReferencia: precioReferencia,
            descuentoConIGV: descuentoConIGV,
            descuentoSinIGV: descuentoSinIGV,
            descuentoIGV: descuentoIGV,
            comentario: x.comentario,
            flagGratuito: x.flagGratuito,
            afectacion: x.afectacion,
            flagAnticipo: x.flagAnticipo
          }
        })

      if (detallesNormalizados.length === 0) {
        __zariContext.snack('error', 'No se han registrado detalles.')
        setSubmitting(false)
        return
      }

      const submitData = {
        ...formData,
        _idSocio: formData._idSocio,
        numeroDocumento: parseFloat(formData.numeroDocumento),
        detalles: detallesNormalizados,
        importeDescuento: parseFloat(resumenMontosRef.current.sumaDescuento),
        importeGratuito: parseFloat(resumenMontosRef.current.sumaGratuito),
        importeGravado: parseFloat(resumenMontosRef.current.sumaGravado),
        importeInafecto: parseFloat(resumenMontosRef.current.sumaInafecto),
        importeExonerado: parseFloat(resumenMontosRef.current.sumaExonerado),
        importeIgv: parseFloat(resumenMontosRef.current.sumaIgv),
        importeTotal: parseFloat(resumenMontosRef.current.sumaTotal)
      }

      const submitFunction = submitData._id
        ? () => PreFacturacionService.actualizarxId(submitData._id, submitData)
        : () => PreFacturacionService.nuevo(submitData)

      submitFunction()
        .then(({ data }) => {
          __zariContext.snack(
            'success',
            `El documento se ha guardado con ID: ${data.identificador} ${data.nombre ? `(${data.nombre})` : ''}.`
          )
          reiniciarFormulario()
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
    },
    [__zariContext, detalles, reiniciarFormulario]
  )

  React.useEffect(() => {
    if (flagEditar) {
      if (!firstRefreshRef.current) {
        clickRecalcular()
        firstRefreshRef.current = true
      }
    }
  }, [clickRecalcular, flagEditar])

  return (
    <>
      <Row>
        <Col md={12}>
          <Formik
            initialValues={formInitialValues}
            innerRef={mainFormikRef}
            onSubmit={onSubmit}
            validationSchema={ValidationSchema}
            validateOnMount={false}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ errors, isSubmitting, touched, values, handleSubmit, setFieldValue }) => (
              <Form>
                <Row>
                  <Col md={5} xl={4}>
                    <FormGroups.NombreFormGroup
                      fIsSubmitting={isSubmitting}
                      iError={errors.nombre}
                      iTouched={touched.nombre}
                    />
                  </Col>
                  <Col md={7} xl={5} />
                  <Col md={3} xl={3}>
                    <FormGroups.IdListaPrecioFormGroup
                      fIsSubmitting={isSubmitting}
                      iError={errors._idListaPrecio}
                      iTouched={touched._idListaPrecio}
                      setFieldValue={setFieldValue}
                      flagEditar={flagEditar}
                    />
                  </Col>
                </Row>
                <Divider />
                <Sections.DetallesSection
                  detalles={detalles}
                  dispatch={detallesDispatcher}
                  tipoProducto={values.tipoProducto}
                  incluyeIGV={values.incluyeIGV}
                  idSocio={values._idSocio}
                  reiniciarFormularioMontos={reiniciarFormularioMontos}
                  idListaPrecio={values._idListaPrecio}
                />
                <Row className='mb-2'>
                  <Col md={12} className='text-right'>
                    {mostrarBotonRecalcular && (
                      <Button disabled={isSubmitting} onClick={clickRecalcular}>
                        Recalcular
                      </Button>
                    )}
                  </Col>
                </Row>
                <Row className='mt-2'>
                  <Col md={12}>
                    <Sections.ResumenMontosSection
                      key={__keyResumenMontos}
                      montos={resumenMontosRef.current}
                      flagVentaRapida={true}
                    />
                  </Col>
                </Row>
                <Divider noMargin />
                <Row>
                  <Col md={12} className='text-right'>
                    <Button disabled={isSubmitting || mostrarBotonRecalcular} onClick={handleSubmit}>
                      Procesar
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </>
  )
}

export default React.memo(EstandarView)
